<template>
	<page>
		<h1>Account Settings</h1>

		<b-row>
			<b-col cols="6">
				<b-card header="Update Profile">
					<b-row>
						<b-col cols="12">
							<b-form-group label="Email">
								<b-form-input type="email" :state="emailState" v-model="$v.profile_form.email.$model" />
								<b-form-invalid-feedback>
									{{ emailError }}
								</b-form-invalid-feedback>
							</b-form-group>
							<b-form-group label="Name">
								<b-form-input type="text" :state="nameState" v-model="$v.profile_form.name.$model" />
								<b-form-invalid-feedback>
									{{ nameError }}
								</b-form-invalid-feedback>
							</b-form-group>
						</b-col>
					</b-row>
					<b-row>
						<b-col cols="12" class="text-right">
							<b-button variant="primary" :disabled="profileButtonDisabled" @click="profileSubmit">Submit</b-button>
						</b-col>
					</b-row>
				</b-card>
			</b-col>
			<b-col cols="6">
				<b-card header="Change Password">
					<b-row>
						<b-col cols="12">
							<b-form-group label="Old Password">
								<b-form-input type="password" :state="oldPasswordState" v-model="$v.password_form.old_password.$model" />
								<b-form-invalid-feedback id="input-live-feedback">
									{{ oldPasswordError }}
								</b-form-invalid-feedback>
							</b-form-group>
						</b-col>
						<b-col cols="12">
							<b-form-group label="New Password">
								<b-form-input type="password" :state="newPasswordState" v-model="$v.password_form.password.$model" />
								<b-form-invalid-feedback id="input-live-feedback">
									{{ newPasswordError }}
								</b-form-invalid-feedback>
								<b-form-text>
									Password must be at least 8 characters and at least 3 of the following: uppercase letter, lowercase letter, digit, special character ( ! @ # $ _ )
								</b-form-text>
							</b-form-group>
						</b-col>
						<b-col cols="12">
							<b-form-group label="Confirm Password">
								<b-form-input type="password" :state="confirmPasswordState" v-model="$v.password_form.password_confirmation.$model" />
								<b-form-invalid-feedback id="input-live-feedback">
									{{ confirmPasswordError }}
								</b-form-invalid-feedback>
							</b-form-group>
						</b-col>
						<b-col cols="12" class="text-right">
							<b-button variant="primary" @click="passwordSubmit">Submit</b-button>
						</b-col>
					</b-row>
				</b-card>
			</b-col>
		</b-row>
	</page>
</template>

<script>
import Page from '../components/layout/Page';
import nprogress from 'nprogress';
import {
	required,
	minLength,
	maxLength,
	sameAs,
	email,
	helpers
} from 'vuelidate/lib/validators';
import ErrorModal from '../mixins/ErrorModal';

import {
	userNameRegex,
	userPasswordRegex
} from '../util/RegularExpressions';

export default {
	components: { Page },
	mixins: [ ErrorModal ],
	data () {
		return {
			password_form: {
				old_password: '',
				password: '',
				password_confirmation: ''
			},
			profile_form: {
				email: '',
				name: ''
			}
		};
	},
	validations: {
		password_form: {
			old_password: {
				required
			},
			password: {
				required,
				minLength: minLength(8),
				confirmed: sameAs('password_confirmation'),
				passwordRegex: helpers.regex('password', userPasswordRegex)
			},
			password_confirmation: {
				required,
				confirmed: sameAs('password')
			}
		},
		profile_form: {
			name: {
				required,
				minLength: minLength(3),
				maxLength: maxLength(50),
				nameRegex: helpers.regex('name', userNameRegex)
			},
			email: {
				required,
				email
			}
		}
	},
	computed: {
		newPasswordError () {
			if (!this.$v.password_form.password.required) {
				return 'New password is required';
			}

			if (!this.$v.password_form.password.minLength) {
				return 'New password must be at least 8 characters long';
			}

			if (!this.$v.password_form.password.passwordRegex) {
				return 'New password does not meet required conventions';
			}

			if (!this.$v.password_form.password.confirmed) {
				return 'New password must match the confirm password field';
			}

			return 'New password is invalid';
		},
		oldPasswordError () {
			if (!this.$v.password_form.old_password.required) {
				return 'Old password is required';
			}

			return 'Old password is invalid';
		},
		confirmPasswordError () {
			if (!this.$v.password_form.password_confirmation.required) {
				return 'Password confirmation is required';
			}

			if (!this.$v.password_form.password_confirmation.confirmed) {
				return 'Password confirmation must match the new password field';
			}

			return 'Password confirmation is invalid';
		},
		nameError () {
			if (!this.$v.profile_form.name.required) {
				return 'Name is required';
			}

			if (!this.$v.profile_form.name.minLength) {
				return 'Name must be at least 3 characters long';
			}

			if (!this.$v.profile_form.name.maxLength) {
				return 'Name can be a max of 50 characters long';
			}

			if (!this.$v.profile_form.name.nameRegex) {
				return 'Name can only contain letters, spaces, dashes, and apostrophes';
			}

			return 'Name is invalid';
		},
		emailError () {
			if (!this.$v.profile_form.email.required) {
				return 'Email is required';
			}

			if (!this.$v.profile_form.email.email) {
				return 'Email is not a valid email address';
			}

			return 'Email is invalid';
		},
		oldPasswordState () {
			if (!this.$v.password_form.old_password.$dirty) {
				return null;
			}

			return !this.$v.password_form.old_password.$invalid;
		},
		newPasswordState () {
			if (!this.$v.password_form.password.$dirty) {
				return null;
			}

			return !this.$v.password_form.password.$invalid;
		},
		confirmPasswordState () {
			if (!this.$v.password_form.password_confirmation.$dirty) {
				return null;
			}

			return !this.$v.password_form.password_confirmation.$invalid;
		},
		nameState () {
			if (!this.$v.profile_form.name.$dirty) {
				return null;
			}

			return !this.$v.profile_form.name.$invalid;
		},
		emailState () {
			if (!this.$v.profile_form.email.$dirty) {
				return null;
			}

			return !this.$v.profile_form.email.$invalid;
		},
		currentUser () {
			return this.$store.getters['auth/currentUser'];
		},
		profileButtonDisabled () {
			return this.currentUser.email === this.profile_form.email && this.currentUser.name === this.profile_form.name;
		}
	},
	mounted () {
		this.profile_form.email = this.currentUser.email;
		this.profile_form.name = this.currentUser.name;

		nprogress.done();
	},
	methods: {
		passwordSubmit () {
			this.$v.password_form.$touch();

			if (!this.$v.password_form.$invalid) {
				this.$store.dispatch('users/updatePassword', this.password_form)
					.then(data => {
						this.passwordFormReset();
						this.$bvModal.msgBoxOk('Password successfully updated', {
							title: 'Success',
							headerBgVariant: 'success',
							okVariant: 'success',
							headerTextVariant: 'white'
						});
					})
					.catch(err => {
						this.showErrorModal(err, 'Error Saving Password');
					});
			}
		},
		passwordFormReset () {
			this.password_form.old_password = '';
			this.password_form.password = '';
			this.password_form.password_confirmation = '';
			this.$v.password_form.$reset();
		},
		profileSubmit () {
			this.$v.profile_form.$touch();

			if (!this.$v.profile_form.$invalid) {
				this.$store.dispatch('users/updateProfile', this.profile_form)
					.then(data => {
						this.$store.commit('auth/UPDATE_USER', data);
						this.profileReset();
						this.$bvModal.msgBoxOk('User profile successfully updated', {
							title: 'Success',
							headerBgVariant: 'success',
							okVariant: 'success',
							headerTextVariant: 'white'
						});
					})
					.catch(err => {
						this.showErrorModal(err, 'Error Updating User Profile');
					});
			}
		},
		profileReset () {
			this.profile_form.name = this.currentUser.name;
			this.profile_form.email = this.currentUser.email;

			this.$v.profile_form.$reset();
		}
	}
};
</script>
